<script setup lang="ts">
import { computed } from 'vue';

/**
 * A status pill.
 *
 * `dot` adds a leading indicator, and `pulse` animates it for genuinely live states.
 * Colour alone never carries the meaning — the label is always present — so the badge
 * remains readable in monochrome and to colour-blind users.
 */
const props = withDefaults(
  defineProps<{
    tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'nvidia' | 'amd';
    size?: 'sm' | 'md';
    dot?: boolean;
    pulse?: boolean;
  }>(),
  { tone: 'neutral', size: 'sm' },
);

const TONES = {
  neutral: 'bg-neutral-subtle text-ink-secondary border-neutral-border',
  success: 'bg-success-subtle text-success border-success-border',
  warning: 'bg-warning-subtle text-warning border-warning-border',
  danger: 'bg-danger-subtle text-danger border-danger-border',
  info: 'bg-info-subtle text-info border-info-border',
  accent: 'bg-accent-subtle text-accent border-accent-border',
  nvidia: 'bg-nvidia-subtle text-nvidia border-transparent',
  amd: 'bg-amd-subtle text-amd border-transparent',
} as const;

const classes = computed(() => [
  TONES[props.tone],
  props.size === 'sm' ? 'h-5 px-2 text-2xs' : 'h-6 px-2.5 text-xs',
]);
</script>

<template>
  <span
    class="inline-flex items-center gap-1.5 rounded-full border font-medium uppercase tracking-wider whitespace-nowrap"
    :class="classes"
  >
    <span
      v-if="dot"
      class="h-1.5 w-1.5 rounded-full bg-current"
      :class="pulse ? 'sp-pulse' : ''"
      aria-hidden="true"
    />
    <slot />
  </span>
</template>
